import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
	BulkOperationType,
	getBulkOperationParams,
	getCurrentSorting,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/bulk-action-utils.ts';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile.ts';
import { format } from 'date-fns';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { InvestmentsListQueryDto } from '@dealstack/shared/lib/generated';
import { InvestmentsBulkOperationParams } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/types.ts';
import { downloadInvestmentsExcel } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { mapInvestmentsTableFilterToDto } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investments-filter-map.ts';
import { EligibilityFilter } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/utils/investment.types.ts';

export const useInvestmentsBulkAction = (
	tableDataOperations: TableOperations<InvestmentsListQueryDto>,
	eligibilityFilter?: EligibilityFilter | null
) => {
	const tMipManage = useTranslationMipManage();

	const tableDataOperationsRef = useRef(tableDataOperations);
	const eligibilityFilterRef = useRef(eligibilityFilter);

	useEffect(() => {
		tableDataOperationsRef.current = tableDataOperations;
	}, [tableDataOperations]);

	useEffect(() => {
		eligibilityFilterRef.current = eligibilityFilter;
	}, [eligibilityFilter]);

	const downloadExcelRequest = useMutation({
		mutationFn: downloadInvestmentsExcel,
	});

	const [isLoading, setIsLoading] = useState(false);

	const downloadExcel = async (params: InvestmentsBulkOperationParams) => {
		setIsLoading(true);
		const operationParams = getBulkOperationParams(params);
		const selectedIds = operationParams.ids ?? [];
		const excludedIds = operationParams.excludedIds ?? [];
		const dataOperations = tableDataOperationsRef.current;
		const filter = mapInvestmentsTableFilterToDto(dataOperations);

		const isFilteredOperation = operationParams.operationType === BulkOperationType.filtered;
		const sortBy = getCurrentSorting(params.table, dataOperations);

		downloadExcelRequest.mutate(
			{
				mipId: params.mipId,
				selectedIds: selectedIds.length ? selectedIds : undefined,
				excludedIds: isFilteredOperation && excludedIds.length ? excludedIds : undefined,
				filter,
				sortBy,
			},
			{
				onSuccess: (response) => {
					setIsLoading(false);
					downloadFileFromBlob(
						response,
						`investments-export-${format(new Date(), 'yyyy-MM-dd')}.xlsx`
					);
				},
				onError: () => {
					setIsLoading(false);
					showToastV2({
						state: 'error',
						message: tMipManage(
							'investments.toasts.errors.failed_download_investments'
						),
					});
				},
			}
		);
	};

	const downloadOneSchemaAllocationsTemplate = async (
		params: InvestmentsBulkOperationParams
	): Promise<boolean> => {
		setIsLoading(true);
		const operationParams = getBulkOperationParams(params);
		const selectedIds = operationParams.ids ?? [];
		const excludedIds = operationParams.excludedIds ?? [];
		const dataOperations = tableDataOperationsRef.current;
		const filter = mapInvestmentsTableFilterToDto(
			dataOperations,
			eligibilityFilterRef.current ?? undefined
		);

		const isFilteredOperation = operationParams.operationType === BulkOperationType.filtered;
		const sortBy = getCurrentSorting(params.table, dataOperations);

		try {
			const response = await downloadExcelRequest.mutateAsync({
				mipId: params.mipId,
				selectedIds: selectedIds.length ? selectedIds : undefined,
				excludedIds: isFilteredOperation && excludedIds.length ? excludedIds : undefined,
				filter,
				sortBy,
				oneSchemaAllocationsTemplate: true,
			});
			downloadFileFromBlob(
				response,
				`allocations-update-${format(new Date(), 'yyyy-MM-dd')}.csv`
			);
			return true;
		} catch {
			showToastV2({
				state: 'error',
				message: tMipManage('investments.toasts.errors.failed_download_investments'),
			});
			return false;
		} finally {
			setIsLoading(false);
		}
	};

	return {
		downloadExcel,
		downloadOneSchemaAllocationsTemplate,
		isLoading,
	};
};
